
import React from 'react';

interface HandProps {
    angle: number;
    color: string;
    label: string;
    subLabel?: string;
    index: number;
    total: number;
    isMoving?: boolean;
}

export const Hand: React.FC<HandProps> = ({ angle, color, label, subLabel, index, total, isMoving }) => {
    // Determine Z-Index
    const zIndex = total - index;

    // Deterministic Skin Tone based on label
    const SKIN_TONES = ['#ffdbac', '#f1c27d', '#e0ac69', '#c68642', '#8d5524', '#3c2e28'];
    const charCodeSum = label.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const skinTone = SKIN_TONES[charCodeSum % SKIN_TONES.length];

    return (
        <div
            className="absolute top-1/2 left-1/2 w-0 h-0 flex items-center justify-center transition-all duration-300 ease-spring"
            style={{
                zIndex: zIndex,
                transform: `rotate(${angle}deg)`,
            }}
        >
            {/* 
                Arm Container 
                - user wants 66% smaller. scale-33 or scale-40.
                - Hands must be ON TOP of each other.
                - Hand SVG: Arm is Left (-40) to Right (160). Hand is at ~160.
                - To pile hands in center, we need x=160 to be at (0,0).
                - So we translate X by -160px.
                - But we also want the arm to radiate OUT.
                - If we rotate 0deg, and translate -160, the Hand is at 0, and Arm extends to -200 (Left).
                - This works!
            */}
            <div
                className={`relative flex flex-col items-center justify-center transition-transform duration-300 ${isMoving ? 'drop-shadow-2xl translate-y-[-10px]' : ''}`}
                style={{
                    transformOrigin: 'center center',
                    // Shift SVG so the palm (x=160) is at center. 
                    // SVG is 240w. Center is 120. Palm is 160. Diff is 40.
                    // So translateX(-40px).
                    transform: isMoving ? 'translateX(-40px) scale(0.38)' : 'translateX(-40px) scale(0.35)'
                }}
            >
                {/* SVG ARM - Styled like Reference (Webtoon/WikiHow) */}
                <svg width="240" height="120" viewBox="0 0 240 120" className="drop-shadow-xl filter overflow-visible">
                    <defs>
                        <filter id="dropshadow" height="130%">
                            <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
                            <feOffset dx="2" dy="4" result="offsetblur" />
                            <feComponentTransfer>
                                <feFuncA type="linear" slope="0.3" />
                            </feComponentTransfer>
                            <feMerge>
                                <feMergeNode />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    <g filter="url(#dropshadow)">
                        {/* SLEEVE */}
                        {/* A tapered cylinder with a distinct cuff */}
                        {/* Extend sleeve continuously to left for varied arm lengths if needed, but fixed is fine */}
                        <path
                            d="M-100,30 L160,35 L160,85 L-100,90 Z"
                            className={`fill-current ${color.replace('bg-', 'text-')}`}
                            stroke="black"
                            strokeWidth="3"
                        />
                        {/* Cuff detail line */}
                        <path d="M130,34 L130,86" stroke="black" strokeWidth="2" strokeOpacity="0.3" fill="none" />


                        {/* HAND (Generic Skin Tone) - RIGHT HAND (Palm Down) */}
                        {/* Thumb should be at TOP (Lower Y) for Right Hand coming from Left */}
                        <g transform="translate(160, 30)">
                            {/* Wrist connection */}
                            <path
                                d="M-5,5 L20,5 L20,55 L-5,55 Z"
                                fill={skinTone}
                            />

                            {/* Main Palm/Hand Shape */}
                            {/* Flipped curvature for Right Hand */}
                            <path
                                d="M0,5 L40,0 C50,0 60,5 60,15 L60,45 C60,55 50,60 40,60 L0,55 Z"
                                fill={skinTone}
                                stroke="black"
                                strokeWidth="3"
                                strokeLinejoin="round"
                            />

                            {/* Fingers (Index, Middle, Ring, Pinky) - NOW AT BOTTOM */}
                            {/* Index (Top-most finger) */}
                            <path d="M40,10 L85,8 C90,8 92,2 85,0 L40,5" fill={skinTone} stroke="black" strokeWidth="3" strokeLinecap="round" />
                            {/* Middle */}
                            <path d="M45,25 L90,25 C95,25 97,19 90,17 L45,20" fill={skinTone} stroke="black" strokeWidth="3" strokeLinecap="round" />
                            {/* Ring */}
                            <path d="M45,40 L85,41 C90,41 92,35 85,33 L45,35" fill={skinTone} stroke="black" strokeWidth="3" strokeLinecap="round" />
                            {/* Pinky (Bottom-most) */}
                            <path d="M40,55 L75,57 C80,57 82,53 75,50 L40,50" fill={skinTone} stroke="black" strokeWidth="3" strokeLinecap="round" />

                            {/* Thumb (Left Side / Top) - RIGHT HAND has thumb "Up" relative to palm center? */}
                            {/* Actually, if palm is down, thumb sticks out to the LEFT of the hand axis? */}
                            {/* No, if arm is L->R. Thumb is TOP. */}
                            <path d="M10,5 Q20,-5 30,5" fill="none" stroke="black" strokeWidth="3" strokeLinecap="round" />

                            {/* Nails/Details (Flipped Y) */}
                            <path d="M80,4 L83,4" stroke="black" strokeWidth="2" opacity="0.3" />
                            <path d="M85,21 L88,21" stroke="black" strokeWidth="2" opacity="0.3" />
                            <path d="M80,37 L83,37" stroke="black" strokeWidth="2" opacity="0.3" />
                        </g>
                    </g>
                </svg>

                {/* LABEL ON SLEEVE */}
                <div
                    className="absolute top-1/2 left-[40px] -translate-y-1/2 -rotate-90 text-white font-black text-xs tracking-wider opacity-90 pointer-events-none whitespace-nowrap origin-center"
                    style={{ textShadow: '1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}
                >
                    {label}
                </div>

                {/* FLOOR COUNT BADGE (Floating on Hand) */}
                {subLabel && (
                    <div
                        className="absolute right-[20px] top-1/2 -translate-y-1/2 z-50 animate-bounce"
                        style={{ transform: `rotate(${-angle}deg)` }} // Counter-rotate to keep text level? Or just let it rotate. Let's counter-rotate for readability.
                    >
                        <div className="bg-white text-black font-black text-lg px-2 py-1 rounded-lg border-2 border-black shadow-[0_4px_0_rgba(0,0,0,0.2)]">
                            {subLabel}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
